import { useSession } from '@batiflow/client-core/react';
import { TRANSMITTAL_LANGUAGES, type TransmittalLanguage } from '@batiflow/contracts';
import { FormField, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Radio,
  RadioGroup,
  Textarea,
  __css,
  tokens,
} from '@fluentui/react-components';
import { useMemo, useRef, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { useNotify } from '@/shared/hooks/use-notify';
import { useRecipientCandidates, useSendTransmittal } from './api';
import {
  MAX_DOCUMENTS,
  MAX_RECIPIENTS,
  buildRequest,
  hasNewerBeingChecked,
  keyFor,
  partitionDocuments,
  sendFailure,
  type BuiltRequest,
  type EmailDraft,
  type SendFailure,
  type TransmittalDocument,
} from './model';
import { RecipientFields } from './recipient-fields';

const useStyles = __css({"surface":{"D305fb":"f17krk77","Bns2gvi":"f1c209jx"},"form":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f13377fj"},"intro":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"},"textarea":{"Bns2gvi":"f1c209jx"},"heading":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","Bg96gwp":"f1i3iumi","Bhrd7zp":"fl43uef"},"documents":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8zks9i","B1hrpyx":"f1jat206","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f778t4r","mlrwpw":"fjnihso","Bmxbyg5":"f5zp4f"},"document":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f1guyajx","i8kkvl":"fsnqrgy","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f18qryls","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","De3pzq":"f1dmdbja"},"code":{"Bahqtrf":"f1fedwem","Bhrd7zp":"fl43uef"},"title":{"Btd35i7":"fjksvth"},"muted":{"sj55zd":"f11d4kpn","Be2twd7":"fy9rknc"},"warn":{"sj55zd":"f1k5f75o","Be2twd7":"fy9rknc"},"problems":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8zks9i","B1hrpyx":"f17mo7c8"},"invalid":{"sj55zd":"f1hcrxcs","Be2twd7":"fy9rknc"}});

export interface SendTransmittalDialogProps {
  spaceId: string;
  /** The documents chosen in the list (or the open one). Those without a version are set aside. */
  documents: readonly TransmittalDocument[];
  onClose: () => void;
  /** The transmittal was sent (the caller clears its selection). */
  onSent?: (numberLabel: string) => void;
}

const SUBJECT_MAX = 200;
const MESSAGE_MAX = 2000;

/**
 * "Envoyer avec un bordereau" (GEDX-FE-02): subject, optional message, language, the recipients
 * (people of the space and e-mail addresses) and the documents with the index that will be sent.
 * The API numbers the transmittal; its refusals (a version, a recipient, a guest domain) are told
 * in plain words naming the document or the person.
 */
export function SendTransmittalDialog({
  spaceId,
  documents,
  onClose,
  onSent,
}: SendTransmittalDialogProps) {
  const { t } = useTranslation('transmittals');
  const { t: tCommon } = useTranslation('common');
  const styles = useStyles();
  const notify = useNotify();
  const locale = useLocale();
  const userId = useSession((state) => state.me?.user.id ?? null);
  const candidates = useRecipientCandidates(spaceId);
  const send = useSendTransmittal(spaceId);
  const key = useRef<{ payload: string; key: string } | null>(null);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [language, setLanguage] = useState<TransmittalLanguage>(locale === 'ar' ? 'ar' : 'fr');
  const [people, setPeople] = useState<string[]>([]);
  const [emails, setEmails] = useState<EmailDraft[]>([]);
  const [attempted, setAttempted] = useState(false);
  const [failure, setFailure] = useState<SendFailure | null>(null);

  const { sendable, unavailable } = partitionDocuments(documents);
  const options = useMemo(() => {
    const seen = new Set<string>();
    return (candidates.data?.items ?? []).flatMap((member) => {
      if (member.userId === userId || seen.has(member.userId)) return [];
      seen.add(member.userId);
      return [{ id: member.userId, label: member.displayName }];
    });
  }, [candidates.data, userId]);
  const chosenPeople = people.flatMap((id) => {
    const option = options.find((candidate) => candidate.id === id);
    return option ? [option] : [];
  });

  const recipientCount = chosenPeople.length + emails.length;
  const subjectError = subject.trim() === '' ? t('send.subject.required') : null;
  const documentsError =
    sendable.length === 0
      ? t('documents.noneSendable')
      : sendable.length > MAX_DOCUMENTS
        ? t('documents.tooMany', { max: MAX_DOCUMENTS })
        : null;
  const recipientsError =
    recipientCount === 0
      ? t('recipients.none')
      : recipientCount > MAX_RECIPIENTS
        ? t('recipients.tooMany', { max: MAX_RECIPIENTS })
        : null;
  const valid = !subjectError && !documentsError && !recipientsError;

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (send.isPending) return;
    setAttempted(true);
    if (!valid) return;
    setFailure(null);
    const request: BuiltRequest = buildRequest({
      subject,
      message,
      language,
      documents,
      people: chosenPeople,
      emails,
    });
    key.current = keyFor(key.current, request.data, () => crypto.randomUUID());
    try {
      const transmittal = await send.mutateAsync({
        data: request.data,
        idempotencyKey: key.current.key,
      });
      notify({
        intent: 'success',
        message: t('send.sent', { number: isolate(transmittal.numberLabel) }),
      });
      onSent?.(transmittal.numberLabel);
      onClose();
    } catch (error) {
      setFailure(sendFailure(t, tCommon, error, request));
    }
  };

  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open && !send.isPending) onClose();
      }}
    >
      <DialogSurface className={styles.surface}>
        <form
          noValidate
          onSubmit={(event) => {
            void submit(event);
          }}
        >
          <DialogBody>
            <DialogTitle>{t('send.title')}</DialogTitle>
            <DialogContent>
              <div className={styles.form}>
                <p className={styles.intro}>{t('send.intro')}</p>
                {failure ? (
                  <MessageBar intent="error" layout="multiline" role="alert">
                    <MessageBarBody>
                      <MessageBarTitle>{failure.title}</MessageBarTitle>
                      {failure.lines.length === 1 ? (
                        failure.lines[0]
                      ) : (
                        <ul className={styles.problems}>
                          {failure.lines.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      )}
                      {failure.traceId ? (
                        <span className={styles.muted}>
                          {' '}
                          {tCommon('errors.traceId', { traceId: isolate(failure.traceId) })}
                        </span>
                      ) : null}
                    </MessageBarBody>
                  </MessageBar>
                ) : null}

                <FormField
                  required
                  label={t('send.subject.label')}
                  hint={t('send.subject.hint')}
                  error={attempted ? subjectError : null}
                >
                  <Input
                    maxLength={SUBJECT_MAX}
                    value={subject}
                    onChange={(_event, data) => {
                      setSubject(data.value);
                    }}
                  />
                </FormField>
                <FormField label={t('send.message.label')} hint={t('send.message.hint')}>
                  <Textarea
                    className={styles.textarea}
                    maxLength={MESSAGE_MAX}
                    value={message}
                    onChange={(_event, data) => {
                      setMessage(data.value);
                    }}
                  />
                </FormField>
                <FormField label={t('send.language.label')}>
                  <RadioGroup
                    layout="horizontal"
                    value={language}
                    onChange={(_event, data) => {
                      if ((TRANSMITTAL_LANGUAGES as readonly string[]).includes(data.value))
                        setLanguage(data.value === 'ar' ? 'ar' : 'fr');
                    }}
                  >
                    {TRANSMITTAL_LANGUAGES.map((code) => (
                      <Radio key={code} value={code} label={t(`send.language.${code}`)} />
                    ))}
                  </RadioGroup>
                </FormField>

                <section aria-labelledby="send-documents-title">
                  <h3 id="send-documents-title" className={styles.heading}>
                    {t('documents.title', { count: sendable.length })}
                  </h3>
                  <ul className={styles.documents} aria-label={t('documents.listLabel')}>
                    {sendable.map((document) => (
                      <li key={document.id} className={styles.document}>
                        <bdi dir="ltr" className={styles.code}>
                          {document.code ?? t('documents.noCode')}
                        </bdi>
                        <bdi className={styles.title}>{document.title}</bdi>
                        <span className={styles.muted}>
                          {document.revisionIndex
                            ? t('documents.index', { index: isolate(document.revisionIndex) })
                            : t('documents.noIndex')}
                        </span>
                        {hasNewerBeingChecked(document) && document.revisionIndex ? (
                          <span className={styles.warn}>
                            {t('documents.newerChecking', {
                              index: isolate(document.revisionIndex),
                            })}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {unavailable.length > 0 ? (
                    <MessageBar intent="warning" layout="multiline">
                      <MessageBarBody>
                        {t('documents.unavailableNote', { count: unavailable.length })}
                        <ul className={styles.problems}>
                          {unavailable.map((document) => (
                            <li key={document.id}>
                              <bdi>{document.title}</bdi>{' '}
                              <span className={styles.muted}>{t('documents.unavailable')}</span>
                            </li>
                          ))}
                        </ul>
                      </MessageBarBody>
                    </MessageBar>
                  ) : null}
                  {attempted && documentsError ? (
                    <span role="alert" className={styles.invalid}>
                      {documentsError}
                    </span>
                  ) : null}
                </section>

                <RecipientFields
                  options={options}
                  optionsFailed={candidates.isError}
                  people={people}
                  onPeopleChange={setPeople}
                  emails={emails}
                  onEmailsChange={setEmails}
                  error={attempted ? recipientsError : null}
                />
                {attempted && !valid ? (
                  <span role="alert" className={styles.invalid}>
                    {t('send.formInvalid')}
                  </span>
                ) : null}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" disabled={send.isPending} onClick={onClose}>
                {tCommon('actions.cancel')}
              </Button>
              <Button type="submit" appearance="primary" disabledFocusable={send.isPending}>
                {send.isPending ? t('send.sending') : t('send.submit')}
              </Button>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/transmittals/send-dialog.griffel.css";